import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactDatePicker from 'react-datepicker';
import { REPORT_TYPE_CREATE_OPTIONS } from '../narrativeReportsConfig';
import { datePickerClass } from '../../../utilities/common';

const monthStringToDate = (value) => {
  if (!value) return null;
  const [year, month] = value.split('-');
  return new Date(Number(year), Number(month) - 1, 1);
};

const dateToMonthString = (value) => {
  if (!value) return '';
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

const monthPickerClass = (invalid = false) => {
  const classProps = datePickerClass(invalid);
  return {
    ...classProps,
    wrapperClassName: `${classProps.wrapperClassName} narrative-create-month-picker`,
  };
};

const CreateNarrativeReportModal = ({ show, onClose, onCreate, isLoading, fixedFramework }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [framework, setFramework] = useState(fixedFramework || '');
  const [startDate, setStartDate] = useState(`${currentYear - 1}-01`);
  const [endDate, setEndDate] = useState(`${currentYear - 1}-12`);
  const [baseYear, setBaseYear] = useState(currentYear - 2);
  const [reportYear, setReportYear] = useState(currentYear - 1);
  const [name, setName] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const isCsrd = framework === 'CSRD';

  useEffect(() => {
    if (show) {
      setFramework(fixedFramework || '');
      setName('');
      setBaseYear(currentYear - 2);
      setReportYear(currentYear - 1);
      setShowValidation(false);
    }
  }, [show, fixedFramework, currentYear]);

  const frameworkOptions = useMemo(() => REPORT_TYPE_CREATE_OPTIONS, []);

  const monthLabel = (value) => {
    if (!value) return '';
    const [year, month] = value.split('-');
    return new Date(Number(year), Number(month) - 1).toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const periodValid = isCsrd ? (baseYear && reportYear && baseYear < reportYear) : (startDate && endDate);
    if (!name || !framework || !periodValid) {
      setShowValidation(true);
      return;
    }
    if (isCsrd) {
      onCreate({
        name,
        framework,
        startDate: `${baseYear}-01-01`,
        endDate: `${reportYear}-12-31`,
        reportingPeriod: `Base Year: ${baseYear} | Report Year: ${reportYear}`,
      });
    } else {
      onCreate({
        name,
        framework,
        startDate,
        endDate,
        reportingPeriod: `${monthLabel(startDate)} - ${monthLabel(endDate)}`,
      });
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" dialogClassName="narrative-create-modal">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="narrative-modal-header">
          <div>
            <Modal.Title>{t('Create New Report')}</Modal.Title>
            <div className="text-muted">{t('Configure your climate-related financial disclosure report')}</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {showValidation && (
            <Alert variant="warning">
              {t('Please complete all required fields before starting the report.')}
            </Alert>
          )}
          <Form.Group>
            <Form.Label>
              {t('Select Reporting Framework')} <span className="text-danger">*</span>
              {fixedFramework && <span className="text-muted ml-2">{t('(Fixed for cloned report)')}</span>}
            </Form.Label>
            <Form.Control
              as="select"
              value={framework}
              disabled={!!fixedFramework}
              isInvalid={showValidation && !framework}
              onChange={(event) => {
                setFramework(event.target.value);
                setShowValidation(false);
              }}
            >
              <option value="">{t('Choose a reporting framework...')}</option>
              {frameworkOptions.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.title} - {item.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label>{t('Report Name')} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              value={name}
              placeholder={t('e.g., Climate Disclosure Report - 2024 Annual')}
              isInvalid={showValidation && !name}
              onChange={(event) => {
                setName(event.target.value);
                setShowValidation(false);
              }}
            />
          </Form.Group>
          {isCsrd ? (
            <>
              <Form.Group style={{ marginBottom: '5px' }}>
                <Form.Label>{t('Select Report Year')} <span className="text-danger">*</span></Form.Label>
              </Form.Group>
              <Form.Row>
                <Form.Group className="col-md-6">
                  <Form.Label>{t('Base Year')} <span className="text-danger">*</span></Form.Label>
                  <ReactDatePicker
                    showYearPicker
                    showIcon
                    dateFormat="yyyy"
                    maxDate={new Date(reportYear - 1, 0, 1)}
                    placeholderText={t('Select Base Year')}
                    selected={baseYear ? new Date(baseYear, 0, 1) : null}
                    onChange={(value) => {
                      setBaseYear(value?.getFullYear() || null);
                      setShowValidation(false);
                    }}
                    {...monthPickerClass(showValidation && !baseYear)}
                  />
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>{t('Report Year')} <span className="text-danger">*</span></Form.Label>
                  <ReactDatePicker
                    showYearPicker
                    showIcon
                    dateFormat="yyyy"
                    minDate={new Date((baseYear || currentYear - 2) + 1, 0, 1)}
                    placeholderText={t('Select Report Year')}
                    selected={reportYear ? new Date(reportYear, 0, 1) : null}
                    onChange={(value) => {
                      setReportYear(value?.getFullYear() || null);
                      setShowValidation(false);
                    }}
                    {...monthPickerClass(showValidation && !reportYear)}
                  />
                </Form.Group>
              </Form.Row>
            </>
          ) : (
            <>
              <Form.Group style={{ marginBottom: '5px' }}>
                <Form.Label>{t('Select Report Period')} <span className="text-danger">*</span></Form.Label>
              </Form.Group>
              <Form.Row>
                <Form.Group className="col-md-6">
                  <Form.Label>{t('Start')} <span className="text-danger">*</span></Form.Label>
                  <ReactDatePicker
                    showMonthYearPicker
                    showIcon
                    dateFormat="MMM yyyy"
                    maxDate={monthStringToDate(endDate)}
                    popperPlacement="bottom-end"
                    placeholderText={t('Pick Start Date')}
                    selected={monthStringToDate(startDate)}
                    onChange={(value) => {
                      setStartDate(dateToMonthString(value));
                      setShowValidation(false);
                    }}
                    {...monthPickerClass(showValidation && !startDate)}
                  />
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>{t('End')} <span className="text-danger">*</span></Form.Label>
                  <ReactDatePicker
                    showMonthYearPicker
                    showIcon
                    dateFormat="MMM yyyy"
                    minDate={monthStringToDate(startDate)}
                    popperPlacement="bottom-end"
                    placeholderText={t('Pick End Date')}
                    selected={monthStringToDate(endDate)}
                    onChange={(value) => {
                      setEndDate(dateToMonthString(value));
                      setShowValidation(false);
                    }}
                    {...monthPickerClass(showValidation && !endDate)}
                  />
                </Form.Group>
              </Form.Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {t('Start New Report')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateNarrativeReportModal;
